import React, { useState, useEffect, useContext } from 'react';
import { Container, Tabs, Tab, Card, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MainLayout from './MainLayout';
import ClientSetup from './ClientSetup';
import ClientDashboard from './ClientDashboard';
import ClientUserManagement from './ClientUserManagement';
import ClientRoleManagement from './ClientRoleManagement';
import PermissionManagement from './PermissionManagement';
import { AuthContext } from '../context/AuthContext';
import { authAPI } from '../api/api';
import './AdminPanel.css';

/**
 * Admin Panel Component
 * Main administration interface for client management
 */
const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [accessError, setAccessError] = useState(null);
  const [isSessionError, setIsSessionError] = useState(false);
  
  // Verify admin access on mount
  useEffect(() => {
    const verifyAccess = async () => {
      try {
        const sessionRes = await authAPI.checkSession();
        if (sessionRes.data.success && sessionRes.data.user) {
          const userRole = sessionRes.data.user.Role || sessionRes.data.user.role || '';
          if (userRole !== 'Admin') {
            setAccessError('Admin access required. Your role: ' + userRole);
            setIsSessionError(false);
            setUser(null);
            setTimeout(() => navigate('/dashboard'), 2000);
          } else {
            setUser(sessionRes.data.user);
            setAccessError(null);
            setIsSessionError(false);
          }
        } else {
          setAccessError('Session expired. Redirecting to login...');
          setIsSessionError(true);
          setUser(null);
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (err) {
        // Check if it's a 401 session/auth error
        if (err.response?.status === 401) {
          setAccessError('Session expired. Please log in again.');
          setIsSessionError(true);
          setUser(null);
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setAccessError('Failed to verify access. Please log in again.');
          setIsSessionError(true);
          setUser(null);
          setTimeout(() => navigate('/login'), 2000);
        }
      }
    };
    
    verifyAccess();
  }, [navigate]); // Only depend on navigate, not setUser to prevent infinite loops

  const handleClientCreated = () => {
    // Switch to dashboard and refresh
    setRefreshKey(prev => prev + 1);
    setActiveTab('dashboard');
  };

  if (accessError) {
    return (
      <MainLayout>
        <Container fluid className="admin-panel py-4">
          <Alert variant="danger">
            <Alert.Heading>{isSessionError ? 'Session Expired' : 'Access Denied'}</Alert.Heading>
            <p>{accessError}</p>
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  const adminContent = (
    <Container fluid className="admin-panel py-4">
      <div className="admin-header mb-4">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Administration Panel</h1>
            {user && (
              <small className="text-muted">
                Logged in as: {user.Username || user.username} ({user.Role || user.role})
              </small>
            )}
          </div>
          <Button 
            size="sm"
            variant="secondary" 
            onClick={() => navigate('/dashboard')}
            title="Back to Dashboard"
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>

      <Tabs
        id="admin-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 admin-tabs"
      >
        <Tab
          eventKey="dashboard"
          title={
            <span>
              <i className="bi bi-speedometer2 me-2"></i>
              Client Dashboard
            </span>
          }
        >
          <div className="tab-content">
            <ClientDashboard
              key={refreshKey}
              onCreateClick={() => setActiveTab('create')}
            />
          </div>
        </Tab>

        <Tab
          eventKey="create"
          title={
            <span>
              <i className="bi bi-plus-circle me-2"></i>
              New Client
            </span>
          }
        >
          <div className="tab-content">
            <ClientSetup onClientCreated={handleClientCreated} />
          </div>
        </Tab>

        <Tab
          eventKey="users"
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="bi bi-person-check" style={{ fontSize: '16px', color: '#28a745' }}></i>
              User Management
            </span>
          }
        >
          <div className="tab-content">
            <ClientUserManagement key={refreshKey} />
          </div>
        </Tab>

        <Tab
          eventKey="roles"
          title={
            <span>
              <i className="bi bi-shield-lock me-2"></i>
              Role Management
            </span>
          }
        >
          <div className="tab-content">
            <ClientRoleManagement key={refreshKey} />
          </div>
        </Tab>

        <Tab
          eventKey="permissions"
          title={
            <span>
              <i className="bi bi-lock-fill me-2"></i>
              Permissions
            </span>
          }
        >
          <div className="tab-content">
            <PermissionManagement key={refreshKey} />
          </div>
        </Tab>
      </Tabs>
    </Container>
  );

  return <MainLayout>{adminContent}</MainLayout>;
};

export default AdminPanel;
